if (!customElements.get('aov-addon-form')) {
  customElements.define(
    'aov-addon-form',
    class AovAddonForm extends HTMLElement {
      constructor() {
        super();
        this.controls = this.querySelector('[data-aov-addon-controls]');
        this.button = this.querySelector('[data-aov-addon-submit]');
        this.error = this.querySelector('[data-aov-addon-error]');
        this.variantSelect = this.querySelector('[data-aov-variant-select]');
        this.quantityInput = this.querySelector('[data-aov-addon-quantity]');
        this.decreaseButton = this.querySelector('[data-aov-addon-quantity-decrease]');
        this.increaseButton = this.querySelector('[data-aov-addon-quantity-increase]');

        this.button?.addEventListener('click', this.onAdd.bind(this));
        this.variantSelect?.addEventListener('change', this.onVariantChange.bind(this));
        this.quantityInput?.addEventListener('change', this.normalizeQuantity.bind(this));
        this.quantityInput?.addEventListener('input', this.updateQuantityButtons.bind(this));
        this.decreaseButton?.addEventListener('click', this.adjustQuantity.bind(this, -1));
        this.increaseButton?.addEventListener('click', this.adjustQuantity.bind(this, 1));
        this.normalizeQuantity();
      }

      onVariantChange(event) {
        const option = event.target.selectedOptions[0];
        const price = this.closest('.aov-addon-card')?.querySelector('[data-aov-addon-price]');
        if (price && option?.dataset.price) price.textContent = option.dataset.price;

        if (this.quantityInput) {
          if (option?.dataset.max) {
            this.quantityInput.max = option.dataset.max;
          } else {
            this.quantityInput.removeAttribute('max');
          }
          this.normalizeQuantity();
        }
      }

      normalizeQuantity() {
        if (!this.quantityInput) return 1;

        const minimum = Number.parseInt(this.quantityInput.min, 10) || 1;
        const maximum = Number.parseInt(this.quantityInput.max, 10);
        const requested = Number.parseInt(this.quantityInput.value, 10);
        const validRequested = Number.isFinite(requested) ? requested : minimum;
        const quantity = Number.isFinite(maximum)
          ? Math.min(maximum, Math.max(minimum, validRequested))
          : Math.max(minimum, validRequested);

        this.quantityInput.value = String(quantity);
        this.updateQuantityButtons();
        return quantity;
      }

      adjustQuantity(change, event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.quantityInput || event.currentTarget.disabled) return;

        const quantity = this.normalizeQuantity();
        this.quantityInput.value = String(quantity + change);
        this.normalizeQuantity();
      }

      updateQuantityButtons() {
        if (!this.quantityInput) return;

        const minimum = Number.parseInt(this.quantityInput.min, 10) || 1;
        const maximum = Number.parseInt(this.quantityInput.max, 10);
        const quantity = Number.parseInt(this.quantityInput.value, 10);
        const isLoading = Boolean(this.button?.disabled);

        if (this.decreaseButton) {
          this.decreaseButton.disabled = isLoading || !Number.isFinite(quantity) || quantity <= minimum;
        }
        if (this.increaseButton) {
          this.increaseButton.disabled = isLoading || (Number.isFinite(maximum) && quantity >= maximum);
        }
      }

      async onAdd(event) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.controls || !this.button || this.button.disabled) return;

        const variantId = this.variantSelect?.value || this.querySelector('[data-aov-addon-variant-id]')?.value;
        const quantity = this.normalizeQuantity();
        if (!variantId) {
          this.showError('Please choose an available option.');
          return;
        }

        const cartDrawer = document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', String(quantity));

        if (cartDrawer) {
          formData.append(
            'sections',
            cartDrawer
              .getSectionsToRender()
              .map((section) => section.id)
              .join(',')
          );
          formData.append('sections_url', window.location.pathname);
          cartDrawer.setActiveElement(document.activeElement);
        }

        this.setLoading(true);
        this.showError();

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: formData,
          });
          const data = await response.json();

          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'This item could not be added. Please try again.');
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'aov-addon-form',
              productVariantId: formData.get('id'),
              cartData: data,
            });
          }

          if (cartDrawer) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(data);
          } else {
            window.location.assign(window.routes.cart_url);
          }
        } catch (error) {
          this.showError(error.message);
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        if (!this.button) return;
        this.button.disabled = isLoading;
        this.button.classList.toggle('is-loading', isLoading);
        this.button.setAttribute('aria-busy', String(isLoading));
        if (this.quantityInput) this.quantityInput.disabled = isLoading;
        if (this.variantSelect) this.variantSelect.disabled = isLoading;
        this.updateQuantityButtons();
      }

      showError(message = '') {
        if (!this.error) return;
        this.error.textContent = message;
        this.error.hidden = !message;
      }
    }
  );
}

if (!customElements.get('aov-addon-carousel')) {
  customElements.define(
    'aov-addon-carousel',
    class AovAddonCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.track = this.querySelector('[data-aov-carousel-track]');
        this.previousButton = this.querySelector('[data-aov-carousel-previous]');
        this.nextButton = this.querySelector('[data-aov-carousel-next]');
        this.status = this.querySelector('[data-aov-carousel-status]');
        this.cards = Array.from(this.querySelectorAll('.aov-addon-card'));
        if (!this.track || this.cards.length < 2) return;

        this.initialized = true;
        this.scrollContainer = this.closest('cart-drawer-items');
        this.onWheelBound = this.onWheel.bind(this);
        this.onTouchStartBound = this.onTouchStart.bind(this);
        this.onTouchMoveBound = this.onTouchMove.bind(this);
        this.onTouchEndBound = this.onTouchEnd.bind(this);
        this.previousButton?.addEventListener('click', () => this.move(-1));
        this.nextButton?.addEventListener('click', () => this.move(1));
        this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
        this.track.addEventListener('wheel', this.onWheelBound, { passive: false });
        this.track.addEventListener('touchstart', this.onTouchStartBound, { passive: true });
        this.track.addEventListener('touchmove', this.onTouchMoveBound, { passive: false });
        this.track.addEventListener('touchend', this.onTouchEndBound, { passive: true });
        this.track.addEventListener('touchcancel', this.onTouchEndBound, { passive: true });
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updateControls());
          this.resizeObserver.observe(this.track);
        } else {
          this.onWindowResize = () => this.updateControls();
          window.addEventListener('resize', this.onWindowResize);
        }
        this.updateControls();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        if (this.onWindowResize) window.removeEventListener('resize', this.onWindowResize);
        this.track?.removeEventListener('wheel', this.onWheelBound);
        this.track?.removeEventListener('touchstart', this.onTouchStartBound);
        this.track?.removeEventListener('touchmove', this.onTouchMoveBound);
        this.track?.removeEventListener('touchend', this.onTouchEndBound);
        this.track?.removeEventListener('touchcancel', this.onTouchEndBound);
      }

      onWheel(event) {
        if (!this.scrollContainer) return;

        const horizontalDelta = Math.abs(event.deltaX);
        const verticalDelta = Math.abs(event.deltaY);

        // Shift + mouse wheel is an intentional horizontal carousel gesture.
        if (event.shiftKey && verticalDelta > horizontalDelta) {
          this.track.scrollLeft += this.normalizeWheelDelta(event.deltaY, event.deltaMode);
          event.preventDefault();
          return;
        }

        // A primarily vertical trackpad/mouse gesture belongs to the drawer,
        // even when the pointer happens to be over the horizontal carousel.
        if (verticalDelta <= horizontalDelta || verticalDelta === 0) return;

        this.scrollContainer.scrollTop += this.normalizeWheelDelta(event.deltaY, event.deltaMode);
        event.preventDefault();
      }

      normalizeWheelDelta(delta, deltaMode) {
        if (deltaMode === WheelEvent.DOM_DELTA_LINE) return delta * 16;
        if (deltaMode === WheelEvent.DOM_DELTA_PAGE) return delta * this.scrollContainer.clientHeight;
        return delta;
      }

      onTouchStart(event) {
        const touch = event.touches[0];
        if (!touch) return;

        this.touchAxis = null;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.lastTouchY = touch.clientY;
      }

      onTouchMove(event) {
        const touch = event.touches[0];
        if (!touch || !this.scrollContainer) return;

        const totalX = touch.clientX - this.touchStartX;
        const totalY = touch.clientY - this.touchStartY;

        if (!this.touchAxis && Math.max(Math.abs(totalX), Math.abs(totalY)) >= 6) {
          this.touchAxis = Math.abs(totalY) > Math.abs(totalX) ? 'vertical' : 'horizontal';
        }

        if (this.touchAxis !== 'vertical') return;

        const scrollDelta = this.lastTouchY - touch.clientY;
        this.lastTouchY = touch.clientY;
        this.scrollContainer.scrollTop += scrollDelta;
        if (event.cancelable) event.preventDefault();
      }

      onTouchEnd() {
        this.touchAxis = null;
      }

      getStep() {
        const firstCard = this.cards[0];
        if (!firstCard) return this.track.clientWidth;

        const styles = window.getComputedStyle(this.track);
        const gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return firstCard.getBoundingClientRect().width + gap;
      }

      move(direction) {
        this.track.scrollBy({ left: this.getStep() * direction, behavior: 'smooth' });
      }

      queueUpdate() {
        if (this.updateFrame) return;
        this.updateFrame = requestAnimationFrame(() => {
          this.updateFrame = null;
          this.updateControls();
        });
      }

      updateControls() {
        const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const current = Math.max(0, this.track.scrollLeft);
        const atStart = current <= 2;
        const atEnd = current >= maximum - 2;

        if (this.previousButton) this.previousButton.disabled = atStart;
        if (this.nextButton) this.nextButton.disabled = atEnd;

        if (this.status) {
          const index = Math.min(this.cards.length, Math.round(current / this.getStep()) + 1);
          this.status.textContent = `Recommended product ${index} of ${this.cards.length}`;
        }
      }
    }
  );
}

const AOV_CART_MUTATION_GUARD_MS = 8000;
let aovCartMutationGuardUntil = 0;
let aovOrderLimitObserver;

function isAovOrderLimitCheckoutMessage(text = '') {
  const normalizedText = text.replace(/\s+/g, ' ').trim();
  return /(?:\bmoq\b|\bminimum order(?: quantity)?\b).{0,160}\bcheckout\b/i.test(normalizedText);
}

function findAovOrderLimitMessage(root) {
  const element = root?.nodeType === Node.ELEMENT_NODE ? root : root?.parentElement;
  if (!element) return null;

  const candidates = [element, ...element.querySelectorAll('*')];
  for (let index = candidates.length - 1; index >= 0; index -= 1) {
    const candidate = candidates[index];
    if (!isAovOrderLimitCheckoutMessage(candidate.textContent || '')) continue;

    const childHasMessage = Array.from(candidate.children).some((child) =>
      isAovOrderLimitCheckoutMessage(child.textContent || '')
    );
    if (!childHasMessage) return candidate;
  }

  return null;
}

function isAovCloseControl(control) {
  const label = [control.getAttribute('aria-label'), control.getAttribute('title'), control.textContent]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  return ['close', 'dismiss', 'x', '×', '✕'].includes(label);
}

function dismissAovOrderLimitWarning(root) {
  if (Date.now() > aovCartMutationGuardUntil) return false;

  const message = findAovOrderLimitMessage(root);
  if (!message || message.closest('.aov-moq-warning')) return false;

  let container = message;
  let overlay;
  for (let depth = 0; container && container !== document.body && depth < 10; depth += 1) {
    const style = window.getComputedStyle(container);
    const role = container.getAttribute('role');
    const isOverlay = style.position === 'fixed' || role === 'alert' || role === 'dialog';

    if (isOverlay) {
      if (container.hidden || style.display === 'none' || container.getAttribute('aria-hidden') === 'true') return false;
      overlay = container;
      const controls = Array.from(container.querySelectorAll('button, [role="button"]'));
      const closeControl = controls.find(isAovCloseControl) || (style.position === 'fixed' && controls.length === 1 ? controls[0] : null);
      if (closeControl) {
        closeControl.click();
        return true;
      }
    }

    container = container.parentElement;
  }

  if (overlay) {
    overlay.hidden = true;
    overlay.style.display = 'none';
    return true;
  }

  return false;
}

function beginAovCartMutation() {
  aovCartMutationGuardUntil = Date.now() + AOV_CART_MUTATION_GUARD_MS;
  queueMicrotask(() => dismissAovOrderLimitWarning(document.body));
}

function initializeAovCartMutationGuard() {
  if (!document.body || aovOrderLimitObserver) return;

  aovOrderLimitObserver = new MutationObserver((records) => {
    if (Date.now() > aovCartMutationGuardUntil) return;

    for (const record of records) {
      if (record.type === 'characterData' && dismissAovOrderLimitWarning(record.target)) return;
      if (record.type === 'attributes' && dismissAovOrderLimitWarning(record.target)) return;
      for (const node of record.addedNodes) {
        if (dismissAovOrderLimitWarning(node)) return;
      }
    }
  });
  aovOrderLimitObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
  });
}

function showAovMoqWarning(button) {
  const warningId = button?.dataset.aovMoqWarning;
  if (!warningId) return;

  const warning = document.getElementById(warningId);
  if (!warning) return;

  warning.hidden = false;
  warning.classList.remove('is-visible');
  requestAnimationFrame(() => warning.classList.add('is-visible'));
  warning.focus({ preventScroll: true });
}

function syncAovMoqCheckoutLabel(root = document) {
  const button = root.querySelector?.('#CartDrawer-Checkout[data-aov-moq-blocked="true"]');
  const label = button?.dataset.aovMoqLabel;
  if (!button) return;

  button.type = 'button';
  button.disabled = true;
  button.setAttribute('aria-disabled', 'true');
  if (label && button.textContent.trim() !== label) button.textContent = label;
}

function initializeAovMoqCheckout() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;

  syncAovMoqCheckoutLabel(cartDrawer);
  const observer = new MutationObserver(() => syncAovMoqCheckoutLabel(cartDrawer));
  observer.observe(cartDrawer, { childList: true, subtree: true, characterData: true });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeAovMoqCheckout();
      initializeAovCartMutationGuard();
    },
    { once: true }
  );
} else {
  initializeAovMoqCheckout();
  initializeAovCartMutationGuard();
}

document.addEventListener(
  'click',
  (event) => {
    const mutationControl = event.target.closest?.(
      '[data-aov-addon-submit], #CartDrawer-Form .quantity__button, #CartDrawer-Form cart-remove-button, quick-add-bulk .quantity__button, quick-order-list .quantity__button, [data-wsb-summary-action]'
    );
    if (mutationControl) beginAovCartMutation();
  },
  true
);

document.addEventListener(
  'change',
  (event) => {
    if (
      event.target.matches?.(
        '#CartDrawer-Form .quantity__input, quick-add-bulk .quantity__input, quick-order-list .quantity__input'
      )
    ) {
      beginAovCartMutation();
    }
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const checkoutButton = event.target.closest?.(
      '#CartDrawer-Checkout[data-aov-moq-blocked="true"]'
    );
    if (!checkoutButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showAovMoqWarning(checkoutButton);
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    if (!event.target.matches?.('#CartDrawer-Form')) return;

    const checkoutButton = event.target.querySelector('#CartDrawer-Checkout[data-aov-moq-blocked="true"]');
    if (!checkoutButton) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    showAovMoqWarning(checkoutButton);
  },
  true
);

// Quantity edits belong to the cart update flow, never the checkout submit flow.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' || !event.target.matches('#CartDrawer-Form .quantity__input')) return;

  event.preventDefault();
  event.target.dispatchEvent(new Event('change', { bubbles: true }));
});
